// QAKnowledgeBase.js
import React, { Component } from 'react';
import axios from 'axios';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class QAKnowledgeBase extends Component {
    constructor(props) {
        super(props);
            this.state = {
            // docs
            docs: [],
            loadingDocs: false,
            docsError: '',
            nextToken: null,

            // local metadata: s3Uri -> { title, summary }
            docMeta: {},

            // QA
            question: '',
            answer: '',
            loading: false,
            error: '',

            // modal / upload
            showModal: false,
            uploadTitle: '',
            uploadSummary: '',
            uploadFile: null,
            uploadStatus: '',
            uploading: false,

            // editing state
            editingS3Uri: null,
            editTitle: '',
            editSummary: '',
            savingEdit: false,
            editError: '',

            // deleting
            deletingS3Uri: null,
        };
    }

    componentDidMount() {
        this.loadDocuments();
    }

    // --------- HELPERS ---------
    prettyTitle = (uri) => {
        if (!uri) return 'Untitled document';
        const name = uri.split('/').pop();        // last part
        const withoutExt = name.replace(/\.[^.]+$/, ''); // remove extension
        return withoutExt.replace(/[-_]+/g, ' '); // replace -/_ with spaces
    };

    // --------- DOCS ---------
    loadDocuments = async (nextToken = null) => {
        this.setState({ loadingDocs: true, docsError: '' });

        try {
        const res = await axios.get(`${EndPoint}/bedrock/documents`, {
            params: { nextToken },
        });

        this.setState((prev) => ({
            docs: nextToken ? [...prev.docs, ...res.data.documents] : res.data.documents,
            nextToken: res.data.nextToken || null,
            loadingDocs: false,
        }));
        } catch (err) {
        console.error(err);
        this.setState({
            docsError: 'Failed to load documents.',
            loadingDocs: false,
        });
        }
    };

    // ---------- QA handlers ----------
    handleChange = (e) => {
        this.setState({ question: e.target.value });
    };

    handleAsk = async (e) => {
        e.preventDefault();
        const { question } = this.state;

        if (!question.trim()) return;
        this.setState({ loading: true, error: '', answer: '' });

        try {
        const res = await axios.post(`${EndPoint}/bedrock/ask`, { question });
        this.setState({ answer: res.data.answer || '', loading: false });
        } catch (err) {
        console.error(err);
        this.setState({ error: 'Something went wrong.', loading: false });
        }
    };

    // ---------- Modal / upload handlers ----------
    openModal = () => {
        this.setState({
        showModal: true,
        uploadTitle: '',
        uploadSummary: '',
        uploadFile: null,
        uploadStatus: '',
        });
    };

    closeModal = () => {
        if (this.state.uploading) return;
        this.setState({ showModal: false });
    };

    handleUploadFieldChange = (e) => {
        this.setState({ [e.target.name]: e.target.value });
    };

    handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const { uploadTitle, uploadSummary } = this.state;

    this.setState({
        uploadFile: file,
        uploading: true,
        uploadStatus: 'Uploading document and saving metadata…',
    });

    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', uploadTitle || 'Untitled document');
        formData.append('summary', uploadSummary || '');

        const res = await axios.post(
        `${EndPoint}/bedrock/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        this.setState({
        uploadStatus: res.data.status || 'Uploaded successfully.',
        uploading: false,
        });

        // refresh the document list so it shows up on the left
        this.loadDocuments();
    } catch (err) {
        console.error('Upload error:', err);
        this.setState({
        uploadStatus:
            err.response?.data?.error ||
            'Upload failed. Please check the server logs.',
        uploading: false,
        });
    }
    };

    startEditDocument = (doc) => {
        this.setState({
            editingS3Uri: doc.s3Uri,
            editTitle: doc.title || '',
            editSummary: doc.summary || '',
            editError: '',
        });
    };

    cancelEditDocument = () => {
    this.setState({
        editingS3Uri: null,
        editTitle: '',
        editSummary: '',
        savingEdit: false,
        editError: '',
    });
    };

    handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
    };

    saveEditDocument = async () => {
    const { editingS3Uri, editTitle, editSummary, docs } = this.state;
    if (!editingS3Uri) return;

    this.setState({ savingEdit: true, editError: '' });

    try {
        const res = await axios.patch(`${EndPoint}/bedrock/documents`, {
        s3Uri: editingS3Uri,
        title: editTitle,
        summary: editSummary,
        });

        const updatedDoc = res.data.document;

        // Update docs state locally
        const nextDocs = docs.map((d) =>
        d.s3Uri === editingS3Uri
            ? { ...d, title: updatedDoc.title, summary: updatedDoc.summary }
            : d
        );

        this.setState({
        docs: nextDocs,
        editingS3Uri: null,
        editTitle: '',
        editSummary: '',
        savingEdit: false,
        editError: '',
        });
    } catch (err) {
        console.error('Save edit error:', err);
        this.setState({
        savingEdit: false,
        editError:
            err.response?.data?.error || 'Failed to save changes.',
        });
    }
    };

    deleteDocument = async (doc) => {
    const confirmed = window.confirm(
        'Are you sure you want to delete this document from S3 and the knowledge base?'
    );
    if (!confirmed) return;

    this.setState({ deletingS3Uri: doc.s3Uri });

    try {
        await axios.delete(`${EndPoint}/bedrock/documents`, {
        data: { s3Uri: doc.s3Uri }, // axios DELETE body
        });

        // Remove from local state
        this.setState((prev) => ({
        docs: prev.docs.filter((d) => d.s3Uri !== doc.s3Uri),
        deletingS3Uri: null,
        }));
    } catch (err) {
        console.error('Delete error:', err);
        this.setState({ deletingS3Uri: null });
        alert(
        err.response?.data?.error || 'Failed to delete document. Check server logs.'
        );
    }
    };


    render() {
        const {
        docs,
        loadingDocs,
        docsError,
        nextToken,
        docMeta,
        question,
        answer,
        loading,
        error,
        showModal,
        uploadTitle,
        uploadSummary,
        uploadStatus,
        uploading,
        } = this.state;

        return (
        <div className=" mx-20 mt-10 p-4">
            <h2 className="text-3xl text-center font-bold my-10 text-gray-800">
            Knowledge Base Viewer
            </h2>

            <div className="grid grid-cols-2 xs:grid-cols-1 sm:grid-cols-1 gap-6">

            {/* LEFT COLUMN – DOCUMENT LIST */}
            <div className="border border-gray-300 rounded-xl p-4 bg-white shadow-sm h-[75vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="h-5 w-1.5 bg-sky-700 rounded" />
                    <h3 className="text-xl font-semibold">Documents</h3>
                </div>

                <button
                    onClick={this.openModal}
                    className="px-3 py-1.5 rounded-lg bg-sky-800 text-white text-sm font-medium hover:bg-sky-700"
                >
                    Add Document
                </button>
                </div>

                {loadingDocs && (
                <div className="flex items-center gap-3 text-gray-600 mb-2">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Loading documents…
                </div>
                )}

                {docsError && <p className="text-red-600">{docsError}</p>}
                <ul className="space-y-4">
                {docs.map((doc, index) => {
                    const { editingS3Uri, editTitle, editSummary, savingEdit } = this.state;
                    const isEditing = editingS3Uri === doc.s3Uri;

                    if (isEditing) {
                    // EDIT MODE
                    return (
                        <li
                        key={index}
                        className="p-4 border rounded-xl bg-white shadow-sm"
                        >
                        <div className="space-y-3">
                            {/* S3 URI (read-only) */}
                            <div>
                            <p className="text-[11px] text-gray-500 font-mono break-all">
                                {doc.s3Uri}
                            </p>
                            </div>

                            {/* Title input */}
                            <input
                            type="text"
                            name="editTitle"
                            value={editTitle}
                            onChange={this.handleEditFieldChange}
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
                            placeholder="Title"
                            />

                            {/* Summary textarea */}
                            <textarea
                            name="editSummary"
                            rows={3}
                            value={editSummary}
                            onChange={this.handleEditFieldChange}
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
                            placeholder="Short summary"
                            />

                            {/* Buttons */}
                            <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={this.cancelEditDocument}
                                disabled={savingEdit}
                                className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={this.saveEditDocument}
                                disabled={savingEdit}
                                className={`
                                px-3 py-1.5 rounded-lg text-xs text-white
                                ${savingEdit ? 'bg-sky-800/50 cursor-not-allowed' : 'bg-sky-800 hover:bg-sky-700'}
                                `}
                            >
                                {savingEdit ? 'Saving…' : 'Save'}
                            </button>
                            </div>
                        </div>
                        </li>
                    );
                    }

                    // VIEW MODE 
                    const title = doc.title || 'Untitled';
                    const summary = doc.summary || '';
                    const isDeleting = this.state.deletingS3Uri === doc.s3Uri;

                    return (
                    <li
                        key={index}
                        className="p-4 border rounded-xl bg-white shadow-sm hover:bg-gray-50 transition"
                    >
                        <div className="flex items-start justify-between gap-2">
                        <div>
                            {/* Title */}
                            <p className="font-semibold text-gray-900 text-base leading-snug">
                            {title}
                            </p>

                            {/* Summary */}
                            {summary && (
                            <p className="text-gray-700 text-sm mt-1 leading-relaxed">
                                {summary}
                            </p>
                            )}
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col gap-1 ml-2">
                            <button
                            type="button"
                            onClick={() => this.startEditDocument(doc)}
                            className="px-2 py-1 rounded-md text-xs bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100"
                            >
                            Edit
                            </button>
                            <button
                            type="button"
                            onClick={() => this.deleteDocument(doc)}
                            disabled={isDeleting}
                            className={`
                                px-2 py-1 rounded-md text-xs border
                                ${isDeleting
                                ? 'bg-red-100 text-red-400 border-red-200 cursor-not-allowed'
                                : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'}
                            `}
                            >
                            {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                        </div>
                    </li>
                    );

                })}
                </ul>



                {nextToken && (
                <button
                    onClick={() => this.loadDocuments(nextToken)}
                    className="mt-4 w-full py-2 rounded-lg bg-sky-700 text-white hover:bg-sky-600"
                >
                    Load More
                </button>
                )}
            </div>

            {/* RIGHT COLUMN – ASK + ANSWER */}
            <div className="md:col-span-2">
                <form onSubmit={this.handleAsk} className="space-y-3">
                <textarea
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg p-4 text-lg leading-relaxed
                    focus:ring-2 focus:ring-sky-500 focus:outline-none"
                    value={question}
                    onChange={this.handleChange}
                    placeholder="Ask a question about your documents…"
                />

                <button
                    type="submit"
                    disabled={loading || !question.trim()}
                    className={`
                    w-full py-3 px-4 rounded-lg text-white font-semibold text-lg flex items-center justify-center
                    ${loading || !question.trim()
                        ? 'bg-sky-800/50 cursor-not-allowed'
                        : 'bg-sky-800 hover:bg-sky-700'}
                    `}
                >
                    {loading ? (
                    <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Asking…
                    </div>
                    ) : (
                    'Ask'
                    )}
                </button>
                </form>

                {error && <p className="text-red-600 mt-3 text-lg">{error}</p>}

                {answer && (
                <div className="mt-6 p-6 border border-gray-300 rounded-xl bg-white shadow-sm animate-fadeIn">
                    <div className="flex items-center gap-2 mb-4">
                    <div className="h-5 w-1.5 bg-sky-700 rounded" />
                    <strong className="font-semibold text-gray-800 text-xl">
                        Caredevo AI:
                    </strong>
                    </div>

                    <div className="prose prose-slate max-w-none leading-relaxed text-lg space-y-5">
                    {answer.split(/\n+/).map((para, idx) => (
                        <p key={idx} className="whitespace-pre-line text-gray-900">
                        {para.trim()}
                        </p>
                    ))}
                    </div>
                </div>
                )}
            </div>
            </div>

            {/* MODAL – ADD DOCUMENT */}
            {showModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Add Document</h3>
                    <button
                    onClick={this.closeModal}
                    className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                    >
                    ×
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title
                    </label>
                    <input
                        type="text"
                        name="uploadTitle"
                        value={uploadTitle}
                        onChange={this.handleUploadFieldChange}
                        className="w-full border border-gray-300 rounded-lg p-2.5 text-base
                        focus:ring-2 focus:ring-sky-500 focus:outline-none"
                        placeholder="AI will generate a title after you load a file…"
                    />
                    </div>

                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Short Summary
                    </label>
                    <textarea
                        name="uploadSummary"
                        rows={4}
                        value={uploadSummary}
                        onChange={this.handleUploadFieldChange}
                        className="w-full border border-gray-300 rounded-lg p-2.5 text-base
                        focus:ring-2 focus:ring-sky-500 focus:outline-none"
                        placeholder="AI will generate a summary after you load a file…"
                    />
                    </div>

                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Load file
                    </label>
                    <input
                        type="file"
                        accept=".pdf,.txt"
                        onChange={this.handleFileChange}
                        disabled={uploading}
                        className="w-full"
                    />
                    </div>

                    {uploadStatus && (
                    <p className="text-sm text-gray-700">{uploadStatus}</p>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={this.closeModal}
                        disabled={uploading}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={uploading || !uploadTitle.trim()}
                        onClick={this.closeModal}
                        className="px-4 py-2 rounded-lg bg-sky-800 text-white hover:bg-sky-700 disabled:bg-sky-800/40 disabled:cursor-not-allowed"
                    >
                        Add to list
                    </button>
                    </div>
                </div>
                </div>
            </div>
            )}
        </div>
        );
    }
}

export default QAKnowledgeBase;
